// src/services/hermodsPathService.js
// Firebase persistence for Hermod's Path — drafts and post history

import { db } from '../firebase';
import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  getDocs, query, orderBy, where, Timestamp
} from 'firebase/firestore';

const getUserCollection = (userId, collectionName) => {
  if (!userId) throw new Error('userId is required');
  return collection(db, 'users', userId, collectionName);
};

// ── Drafts ──

export async function saveDraft(userId, draftData, existingDraftId) {
  const now = Timestamp.now();

  const docData = {
    rawInput: draftData.rawInput || '',
    contentType: draftData.contentType || 'General',
    platforms: draftData.platforms || [],
    generatedDrafts: draftData.generatedDrafts || {
      professionalIG: null,
      luxLibrisIG: null,
      linkedin: null,
      professionalFB: null,
      luxLibrisFB: null,
    },
    editedDrafts: draftData.editedDrafts || {
      professionalIG: null,
      luxLibrisIG: null,
      linkedin: null,
      professionalFB: null,
      luxLibrisFB: null,
    },
    images: draftData.images || [],
    status: draftData.status || 'draft',
    updatedAt: now,
  };

  if (existingDraftId) {
    const docRef = doc(db, 'users', userId, 'hermodsPathDrafts', existingDraftId);
    await updateDoc(docRef, docData);
    return existingDraftId;
  } else {
    docData.createdAt = now;
    const docRef = await addDoc(getUserCollection(userId, 'hermodsPathDrafts'), docData);
    return docRef.id;
  }
}

export async function getDrafts(userId) {
  const draftsQuery = query(
    getUserCollection(userId, 'hermodsPathDrafts'),
    orderBy('updatedAt', 'desc')
  );
  const snapshot = await getDocs(draftsQuery);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate?.() || new Date(),
    updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
  }));
}

export async function deleteDraft(userId, draftId) {
  const docRef = doc(db, 'users', userId, 'hermodsPathDrafts', draftId);
  await deleteDoc(docRef);
}

export async function updateDraftStatus(userId, draftId, status) {
  if (!userId || !draftId) return;
  const docRef = doc(db, 'users', userId, 'hermodsPathDrafts', draftId);
  await updateDoc(docRef, { status, updatedAt: Timestamp.now() });
}

// ── Post History ──

export async function logCopiedPost(userId, postData) {
  const docData = {
    content: postData.content || {
      professionalIG: null,
      luxLibrisIG: null,
      linkedin: null,
      professionalFB: null,
      luxLibrisFB: null,
    },
    contentType: postData.contentType || 'General',
    platforms: postData.platforms || [],
    copiedAt: Timestamp.now(),
  };

  await addDoc(getUserCollection(userId, 'hermodsPathHistory'), docData);
}

export async function getPostHistory(userId, filters = {}) {
  const constraints = [orderBy('copiedAt', 'desc')];

  if (filters.contentType) {
    constraints.unshift(where('contentType', '==', filters.contentType));
  }

  const historyQuery = query(
    getUserCollection(userId, 'hermodsPathHistory'),
    ...constraints
  );
  const snapshot = await getDocs(historyQuery);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    copiedAt: doc.data().copiedAt?.toDate?.() || new Date(),
  }));
}
